import {
  randomBetween,
  seededRandom,
  type PuzzleCore,
} from "./types.ts";

export type VectorDroneDifficulty =
  | "practice"
  | "insert-coin"
  | "level-up"
  | "developer-mode";

export interface VectorDroneConfig extends Record<string, unknown> {
  windXMin: number;
  windXMax: number;
  windYMin: number;
  windYMax: number;
  targetXMin: number;
  targetXMax: number;
  targetYMin: number;
  targetYMax: number;
  inputMin: number;
  inputMax: number;
  tolerance: number;
  difficulty?: VectorDroneDifficulty;
}

export interface VectorDroneInstance {
  wind: { x: number; y: number };
  target: { x: number; y: number };
}

export interface VectorDroneSubmission extends Record<string, unknown> {
  x: number;
  y: number;
}

export const vectorDroneDefaults: VectorDroneConfig = {
  difficulty: "practice",
  windXMin: 20,
  windXMax: 60,
  windYMin: -25,
  windYMax: 25,
  targetXMin: 80,
  targetXMax: 150,
  targetYMin: 35,
  targetYMax: 105,
  inputMin: -120,
  inputMax: 160,
  tolerance: 8,
};

export const vectorDroneDifficultyLabels: Record<VectorDroneDifficulty, string> = {
  practice: "Practice",
  "insert-coin": "Insert Coin",
  "level-up": "Level Up",
  "developer-mode": "Developer Mode",
};

export function createTimeSeed(now = Date.now()) {
  const randomValue =
    typeof crypto !== "undefined" && "getRandomValues" in crypto
      ? crypto.getRandomValues(new Uint32Array(1))[0]
      : Math.floor(Math.random() * 2_147_483_647);
  return Math.abs((now ^ randomValue) % 2_147_483_647);
}

export function normalizeVectorDroneDifficulty(
  value: unknown,
): VectorDroneDifficulty {
  return value === "insert-coin" ||
    value === "level-up" ||
    value === "developer-mode" ||
    value === "practice"
    ? value
    : "practice";
}

export function resolveVectorDroneConfig(
  config: Partial<VectorDroneConfig> | Record<string, unknown>,
): VectorDroneConfig {
  const merged = { ...vectorDroneDefaults, ...config };
  return {
    difficulty: normalizeVectorDroneDifficulty(merged.difficulty),
    windXMin: finiteNumber(merged.windXMin, vectorDroneDefaults.windXMin),
    windXMax: finiteNumber(merged.windXMax, vectorDroneDefaults.windXMax),
    windYMin: finiteNumber(merged.windYMin, vectorDroneDefaults.windYMin),
    windYMax: finiteNumber(merged.windYMax, vectorDroneDefaults.windYMax),
    targetXMin: finiteNumber(merged.targetXMin, vectorDroneDefaults.targetXMin),
    targetXMax: finiteNumber(merged.targetXMax, vectorDroneDefaults.targetXMax),
    targetYMin: finiteNumber(merged.targetYMin, vectorDroneDefaults.targetYMin),
    targetYMax: finiteNumber(merged.targetYMax, vectorDroneDefaults.targetYMax),
    inputMin: finiteNumber(merged.inputMin, vectorDroneDefaults.inputMin),
    inputMax: finiteNumber(merged.inputMax, vectorDroneDefaults.inputMax),
    tolerance: finiteNumber(merged.tolerance, vectorDroneDefaults.tolerance),
  };
}

export const vectorDroneCore: PuzzleCore<
  VectorDroneConfig,
  VectorDroneInstance,
  VectorDroneSubmission
> = {
  id: "vector-drone",
  schemaVersion: 1,
  generate(config, seed) {
    const resolved = resolveVectorDroneConfig(config);
    const random = seededRandom(seed);
    return {
      wind: {
        x: Math.round(randomBetween(random, resolved.windXMin, resolved.windXMax)),
        y: Math.round(randomBetween(random, resolved.windYMin, resolved.windYMax)),
      },
      target: {
        x: Math.round(randomBetween(random, resolved.targetXMin, resolved.targetXMax)),
        y: Math.round(randomBetween(random, resolved.targetYMin, resolved.targetYMax)),
      },
    };
  },
  validate(config, instance, submission) {
    const resolved = resolveVectorDroneConfig(config);
    const landingX = finiteNumber(submission.x, 0) + instance.wind.x;
    const landingY = finiteNumber(submission.y, 0) + instance.wind.y;
    const distance = Math.hypot(
      landingX - instance.target.x,
      landingY - instance.target.y,
    );
    const success = distance <= resolved.tolerance;
    return {
      success,
      score: Math.max(0, Math.round(100 - distance * 2)),
      feedback: success
        ? "Perfect compensation. The drone reached the extraction ring."
        : `The drone missed by ${distance.toFixed(1)} units. Counter the wind before launching.`,
      details: { landingX, landingY, distance },
    };
  },
};

function finiteNumber(value: unknown, fallback: number) {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : fallback;
}
